import { useState } from 'react';

const regions = [
  {
    name: 'Orange County',
    tagline: 'Serving Irvine, Newport Beach, Anaheim & Beyond',
    phone: '(562) 455-3028',
    address: 'Irvine, CA 92618',
    img: 'https://public.readdy.ai/ai/img_res/5b526efb1af7fe60a69cf75b483de473.jpg',
    color: 'from-orange-500 to-red-500',
    borderColor: 'border-orange-200',
    cities: ['Irvine', 'Newport Beach', 'Anaheim', 'Santa Ana', 'Huntington Beach', 'Costa Mesa', 'Mission Viejo', 'Lake Forest', 'Aliso Viejo', 'Laguna Hills', 'San Clemente', 'Fullerton', 'Garden Grove', 'Orange', 'Yorba Linda', 'Tustin', 'Laguna Niguel', 'Rancho Santa Margarita'],
    desc: 'Our Orange County team serves the entire county from coastal Newport Beach to inland Anaheim and beyond. We know OC neighborhoods, traffic patterns, and building regulations inside and out — ensuring smooth, efficient moves every time.',
  },
  {
    name: 'Coachella Valley',
    tagline: 'Serving Indio, Palm Springs, Palm Desert & Desert Communities',
    phone: '(760) 625-7767',
    address: 'Indio, CA 92201',
    img: 'https://readdy.ai/api/search-image?query=palm%20springs%20california%20desert%20resort%20community%20mountain%20backdrop%20sunny%20blue%20sky%20palm%20trees%20residential%20neighborhood%20aerial%20view%20warm%20golden%20light%20beautiful%20coachella%20valley%20desert%20landscape&width=700&height=420&seq=loc2026cv&orientation=landscape',
    color: 'from-amber-500 to-orange-500',
    borderColor: 'border-amber-200',
    cities: ['Indio', 'Palm Springs', 'Palm Desert', 'La Quinta', 'Rancho Mirage', 'Desert Hot Springs', 'Cathedral City', 'Indian Wells', 'Coachella', 'Bermuda Dunes', 'Thermal', 'Mecca', 'Thousand Palms'],
    desc: 'Our Coachella Valley team is based in Indio and serves the entire desert region. From seasonal snowbird moves to year-round relocations, we\'re the most trusted and responsive movers across the desert communities.',
  },
  {
    name: 'Long Beach & LA Metro',
    tagline: 'Serving Long Beach, Torrance, South Bay & Surrounding LA',
    phone: '(760) 347-2727',
    address: 'Long Beach, CA 90802',
    img: 'https://readdy.ai/api/search-image?query=long%20beach%20california%20downtown%20skyline%20harbor%20view%20palm%20trees%20sunny%20afternoon%20blue%20sky%20ocean%20view%20beautiful%20coastal%20city%20southern%20california%20golden%20hour%20warm%20light%20residential%20neighborhood&width=700&height=420&seq=loc2026lb&orientation=landscape',
    color: 'from-sky-500 to-cyan-500',
    borderColor: 'border-sky-200',
    cities: ['Long Beach', 'Torrance', 'Carson', 'Compton', 'Hawthorne', 'Inglewood', 'Gardena', 'Redondo Beach', 'Manhattan Beach', 'El Segundo', 'Lakewood', 'Signal Hill', 'Cerritos', 'Downey', 'Bellflower'],
    desc: 'Serving the greater Long Beach and South Bay area, our metro team handles local and regional moves across LA County\'s coastal and inland communities with expert efficiency and care.',
  },
];

export default function LocationsGrid() {
  const [activeRegion, setActiveRegion] = useState(0);

  return (
    <>
      {/* Region Cards Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-orange-100 text-orange-600 text-sm font-bold px-4 py-2 rounded-full mb-4">
              Our Service Regions
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-3">Find Your Local Moving Team</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Click a region to see office details, covered cities, and direct contact information for your area.
            </p>
          </div>

          {/* Region Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {regions.map((r, i) => (
              <button
                key={r.name}
                onClick={() => setActiveRegion(i)}
                className={`px-6 py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeRegion === i
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white scale-105'
                    : 'bg-white text-slate-700 border border-gray-200 hover:border-orange-300 hover:text-orange-600'
                }`}
              >
                <i className="ri-map-pin-2-line mr-2"></i>{r.name}
              </button>
            ))}
          </div>

          {/* Active Region Detail */}
          {regions.map((region, i) =>
            i === activeRegion ? (
              <div key={region.name} className={`bg-white rounded-3xl border ${region.borderColor} overflow-hidden`}>
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative">
                    <img
                      src={region.img}
                      alt={`Moving services in ${region.name}, California`}
                      className="w-full object-cover object-top"
                      style={{ height: '400px' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                    <div className="absolute bottom-6 left-6">
                      <div className={`inline-block bg-gradient-to-r ${region.color} text-white text-sm font-bold px-4 py-2 rounded-full`}>
                        <i className="ri-map-pin-fill mr-1"></i>{region.name}
                      </div>
                    </div>
                  </div>
                  <div className="p-8 lg:p-10">
                    <h3 className="text-2xl font-black text-slate-900 mb-2">{region.name}</h3>
                    <p className="text-orange-600 font-semibold text-sm mb-5">{region.tagline}</p>
                    <p className="text-slate-600 text-sm leading-relaxed mb-7">{region.desc}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-7">
                      <a href={`tel:${region.phone.replace(/\D/g, '')}`}
                        className={`flex items-center gap-3 bg-gradient-to-r ${region.color} text-white p-4 rounded-xl hover:opacity-90 transition-all whitespace-nowrap cursor-pointer`}>
                        <i className="ri-phone-fill text-xl flex-shrink-0"></i>
                        <div>
                          <div className="text-xs font-semibold opacity-80">Call this office</div>
                          <div className="font-black text-sm">{region.phone}</div>
                        </div>
                      </a>
                      <div className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-gray-200">
                        <i className="ri-map-pin-2-line text-xl text-slate-500 flex-shrink-0"></i>
                        <div>
                          <div className="text-xs font-semibold text-slate-400">Service Base</div>
                          <div className="font-bold text-slate-800 text-sm">{region.address}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-slate-900 mb-3 flex items-center gap-2">
                        <i className="ri-map-2-line text-orange-500"></i>Cities We Serve
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {region.cities.map((city) => (
                          <span key={city}
                            className="bg-slate-100 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-orange-100 hover:text-orange-700 transition-colors">
                            {city}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null
          )}
        </div>
      </section>

      {/* All Offices Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-slate-900 mb-3">All Office Locations</h2>
            <p className="text-slate-600">3 convenient offices across Southern California — all backed by the same trusted City to City team.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {regions.map((r) => (
              <div key={r.name} className="bg-white rounded-2xl border border-gray-100 p-7 hover:-translate-y-1 transition-all duration-300">
                <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-r ${r.color} rounded-xl mb-5`}>
                  <i className="ri-building-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-1">{r.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{r.address}</p>
                <div className="space-y-3 mb-5">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <i className="ri-phone-line text-orange-500"></i>{r.phone}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <i className="ri-time-line text-orange-500"></i>Mon–Sun: 9:00 AM – 5:00 PM
                  </div>
                </div>
                <a href={`tel:${r.phone.replace(/\D/g, '')}`}
                  className={`block w-full text-center py-3 rounded-xl font-bold text-sm bg-gradient-to-r ${r.color} text-white hover:opacity-90 transition-all whitespace-nowrap cursor-pointer`}>
                  Call This Location
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
